// src/components/game/stages/RecalibrationStage.jsx
import React, { useState } from 'react';
import { ASSETS } from '../../../utils/GameAssets';
import SocialBlackMarket from '../ui/SocialBlackMarket';

const ARTICLES = {
    economic: [
        { id: 'free_market', label: "Laissez-Faire", desc: "Total deregulation.", type: 'Economy' },
        { id: 'planned', label: "Central Planning", desc: "State controls production.", type: 'Economy' },
        { id: 'ubi', label: "Universal Basic Income", desc: "High floor, high taxes.", type: 'Justice' }
    ],
    civil: [
        { id: 'surveillance', label: "Panopticon Security", desc: "Total safety.", type: 'Order' },
        { id: 'liberty', label: "Radical Free Speech", desc: "Chaos is the price.", type: 'Freedom' },
        { id: 'merit', label: "Citizenship by Service", desc: "Service guarantees citizenship.", type: 'Order' }
    ],
    judicial: [
        { id: 'ai_judge', label: "AI Adjudication", desc: "Heartless justice.", type: 'Efficiency' },
        { id: 'jury', label: "Human Juries", desc: "Compassionate but corruptible.", type: 'Humanity' },
        { id: 'corporate', label: "Private Arbitration", desc: "Justice for the highest bidder.", type: 'Economy' }
    ]
};

const POLICY_DB = [
    { id: 'p1', category: 'Population', title: 'Debt Jubilee', text: 'Forgive all consumer debt.', cost: 500, autarky: 5, support: 20, auth: 10 },
    { id: 'p2', category: 'Population', title: 'Austerity', text: 'Cut services to balance books.', cost: 0, autarky: 10, support: -20, auth: -5 },
    { id: 'e1', category: 'Elites', title: 'Asset Seizure', text: 'Nationalize holdings.', cost: 200, autarky: 15, support: 10, auth: -10 },
    { id: 'm1', category: 'Macro', title: 'Pack Courts', text: 'Install loyalist judges.', cost: 300, autarky: 5, support: -10, auth: -30 },
    { id: 'n1', category: 'Emigrants', title: 'Brain Drain Tax', text: 'Charge those who leave.', cost: 100, autarky: 5, support: -5, auth: -10 },
    { id: 'bm1', category: 'Manipulation', title: 'Deepfake Campaign', text: 'Discredit opposition.', cost: 150, autarky: 2, support: 15, auth: -50 }
];

const Scene = ({ bg, children, videoBg }) => (
    <div className="w-full h-full bg-black relative flex flex-col items-center justify-center overflow-hidden p-4">
        {videoBg ? (
            <div className="absolute inset-0 z-0">
                <video src={videoBg} autoPlay loop muted className="w-full h-full object-cover object-top opacity-80" />
            </div>
        ) : (
            bg && <div className="absolute inset-0 z-0"><img src={bg} className="w-full h-full object-cover opacity-40" alt="bg" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/90 z-0"></div>
        <div className="relative z-10 w-full max-w-6xl h-full flex flex-col">{children}</div>
    </div>
);

export function RecalibrationStage({ dispatch, resources, integrity, tools }) {
  const [phase, setPhase] = useState('ELECTORIA_INTRO'); 
  const [draft, setDraft] = useState({ economic: null, civil: null, judicial: null });
  const [govName, setGovName] = useState("");
  const [currentCategory, setCurrentCategory] = useState('Population');
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [showMarket, setShowMarket] = useState(false);

  const calculateGovernment = () => {
      const tags = [draft.economic?.type, draft.civil?.type, draft.judicial?.type];
      if (tags.includes('Economy') && tags.includes('Order')) return "CORPORATE AUTOCRACY";
      if (tags.includes('Justice') && tags.includes('Humanity')) return "SOCIAL DEMOCRACY";
      if (tags.includes('Efficiency') && tags.includes('Economy')) return "ALGORITHMIC TECHNOCRACY";
      return "HYBRID REPUBLIC";
  };

  const getStats = () => {
      let autarky = 0; let support = 50; let auth = integrity; let spent = 0;
      selectedPolicies.forEach(p => { autarky += p.autarky; support += p.support; auth += p.auth; spent += p.cost; });
      return { autarky, support, auth, spent, remaining: resources - spent };
  };
  const stats = getStats();

  const handleDraft = (cat, item) => setDraft(prev => ({ ...prev, [cat]: item }));
  const handleProceed = () => { setGovName(calculateGovernment()); setPhase('POLICY_MAKING'); };
  
  const togglePolicy = (policy) => {
      if (selectedPolicies.includes(policy)) setSelectedPolicies(prev => prev.filter(p => p.id !== policy.id));
      else if (stats.remaining >= policy.cost) setSelectedPolicies(prev => [...prev, policy]);
      else alert("Insufficient Funds");
  };

  const finishGame = () => {
      let status = "FAIL";
      let img = ASSETS.OUTCOMES.Puppet;
      let desc = "The system consumed you.";

      if (stats.autarky < 30) { status = "ECONOMIC COLLAPSE"; img = ASSETS.OUTCOMES.Puppet; desc = "You failed to secure independence."; }
      else if (stats.auth < 20) { status = "SCANDAL & EXILE"; img = ASSETS.OUTCOMES.CancelCulture; desc = "Your manipulations were exposed."; }
      else if (stats.support > 80) { status = "PASSIONARY VICTORY"; img = ASSETS.OUTCOMES.Passionary; desc = "You achieved Autarky."; }
      else { status = "STABLE STATE"; img = ASSETS.OUTCOMES.Troyan; desc = "A fragile peace."; }

      dispatch({ 
          type: 'GAME_OVER', 
          payload: { status, government: govName, scores: { economy: stats.autarky, justice: stats.support, environment: stats.auth }, img, desc } 
      });
  };

  if (phase === 'ELECTORIA_INTRO') {
      return (
          <Scene videoBg={ASSETS.NPCS.Electoratia}>
              <div className="flex flex-col items-center justify-center h-full text-center animate-fade-in">
                  <div className="bg-black/80 border-2 border-indigo-500 p-10 rounded-xl max-w-2xl backdrop-blur-sm">
                      <h3 className="text-4xl text-indigo-400 font-bold mb-6 tracking-widest">ELECTORIA</h3>
                      <p className="text-xl text-white italic mb-8">"The old world is dead. First, we must write the Law. Then, we must execute the Will."</p>
                      <button onClick={() => setPhase('DRAFTING')} className="px-12 py-4 bg-indigo-700 hover:bg-indigo-600 text-white font-bold rounded-full text-xl shadow-lg animate-pulse">DRAFT CONSTITUTION</button>
                  </div>
              </div>
          </Scene>
      );
  }

  if (phase === 'DRAFTING') {
      const isComplete = draft.economic && draft.civil && draft.judicial;
      return (
          <Scene>
              <div className="flex flex-col h-full py-10">
                  <h2 className="text-4xl text-center text-indigo-400 font-bold mb-8 tracking-widest">CONSTITUTIONAL DRAFT</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-grow overflow-y-auto custom-scrollbar px-4">
                      {Object.entries(ARTICLES).map(([cat, items]) => (
                          <div key={cat} className="space-y-3">
                              <h3 className="text-center text-white uppercase font-bold border-b border-gray-700 pb-2">{cat}</h3>
                              {items.map(item => (
                                  <button key={item.id} onClick={() => handleDraft(cat, item)} className={`w-full p-4 text-left border rounded transition-all ${draft[cat]?.id === item.id ? 'bg-indigo-900/50 border-indigo-400' : 'bg-gray-900/80 border-gray-700 hover:border-white'}`}>
                                      <div className="font-bold text-white">{item.label}</div><div className="text-xs text-gray-400 mt-1">{item.desc}</div>
                                  </button>
                              ))}
                          </div>
                      ))}
                  </div>
                  <div className="mt-6 flex justify-center">
                      <button onClick={handleProceed} disabled={!isComplete} className={`px-10 py-4 font-bold rounded text-lg ${isComplete ? 'bg-white text-black hover:scale-105' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`}>RATIFY & FORM GOVERNMENT</button>
                  </div>
              </div>
          </Scene>
      );
  }

  if (phase === 'POLICY_MAKING') {
      let bg = ASSETS.STILLS.Voters; 
      if (currentCategory === 'Elites') bg = ASSETS.STILLS.Competitors;
      if (currentCategory === 'Macro') bg = ASSETS.STILLS.Judges;
      if (currentCategory === 'Emigrants') bg = ASSETS.STILLS.Newcomers;
      if (currentCategory === 'Manipulation') bg = ASSETS.STILLS.Employees;

      const filteredPolicies = POLICY_DB.filter(p => p.category === currentCategory);

      return (
          <Scene bg={bg}>
              {showMarket && (
                  <div className="absolute inset-0 z-50 bg-black/90 p-8 flex items-center justify-center animate-fade-in">
                      <div className="w-full max-w-4xl relative">
                          <button onClick={() => setShowMarket(false)} className="absolute -top-4 -right-4 bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-red-500 font-bold z-50 shadow-lg">X</button>
                          <SocialBlackMarket dispatch={dispatch} currentResources={stats.remaining} ownedTools={tools || []} />
                      </div>
                  </div>
              )}
              <div className="flex flex-col h-full relative">
                  <div className="bg-black/90 border-b border-indigo-500 p-4 flex justify-between items-center gap-4">
                      <div className="grid grid-cols-4 gap-4 text-center flex-grow">
                          <div><div className="text-xs text-gray-500 uppercase">GOV</div><div className="text-indigo-400 font-bold text-sm">{govName}</div></div>
                          <div><div className="text-xs text-gray-500 uppercase">AUTARKY</div><div className={`text-xl font-bold ${stats.autarky >= 30 ? 'text-green-400' : 'text-red-500'}`}>{stats.autarky}/30</div></div>
                          <div><div className="text-xs text-gray-500 uppercase">SUPPORT</div><div className="text-white font-bold text-xl">{stats.support}%</div></div>
                          <div><div className="text-xs text-gray-500 uppercase">FUNDS</div><div className="text-yellow-400 font-bold text-xl">${stats.remaining}</div></div>
                      </div>
                      <button onClick={() => setShowMarket(true)} className="bg-purple-900 border border-purple-500 hover:bg-purple-800 text-purple-200 px-4 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-[0_0_15px_#a855f7] animate-pulse">Black Market</button>
                  </div>
                  <div className="flex bg-gray-900 p-2 overflow-x-auto gap-2">
                      {['Population', 'Elites', 'Macro', 'Emigrants', 'Manipulation'].map(cat => (
                          <button key={cat} onClick={() => setCurrentCategory(cat)} className={`px-4 py-2 rounded text-sm font-bold transition-all ${currentCategory === cat ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>{cat === 'Manipulation' ? '⚠️ OPS' : cat.toUpperCase()}</button>
                      ))}
                  </div>
                  <div className="flex-grow p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredPolicies.map(policy => {
                          const isActive = selectedPolicies.includes(policy);
                          return (
                              <div key={policy.id} onClick={() => togglePolicy(policy)} className={`p-4 border-2 rounded cursor-pointer relative transition-all ${isActive ? 'bg-indigo-900/80 border-indigo-400 shadow-[0_0_15px_#6366f1]' : 'bg-black/80 border-gray-600 hover:border-white'}`}>
                                  <h4 className="font-bold text-white mb-1">{policy.title}</h4>
                                  <p className="text-xs text-gray-300 mb-3 h-8">{policy.text}</p>
                                  <div className="flex justify-between items-end border-t border-gray-700 pt-2">
                                      <span className="text-yellow-500 font-mono text-sm">${policy.cost}</span>
                                      <div className="text-right text-[10px] text-gray-400"><div>AUTARKY +{policy.autarky}</div></div>
                                  </div>
                              </div>
                          );
                      })}
                  </div>
                  <div className="p-4 bg-black/90 border-t border-gray-800 flex justify-center">
                      <button onClick={finishGame} className={`px-10 py-3 rounded font-bold text-xl transition-all ${stats.autarky >= 30 ? 'bg-green-600 hover:bg-green-500 text-white animate-pulse' : 'bg-red-900 text-red-300 hover:bg-red-800'}`}>{stats.autarky >= 30 ? "DECLARE VICTORY" : "FORCE EARLY END"}</button>
                  </div>
              </div>
          </Scene>
      );
  }
  return <div>Loading...</div>;
}